import { FormOverride } from "../../types";
import {
  INCIDENT_INFORMATION_CONSTANTS,
  PHYSICIAN_INFORMATION_CONSTANTS,
  FILE_ATTACHMENTS_CONSTANTS,
  SECONDARY_INJURED_BODY_PART_REQUIRED_ERROR,
} from "../../constants";
import { BODY_SIDE_OPTIONS } from "src/forms/options";
import {
  SEDGWICK_CANADA_CONCERNS_OPTIONS,
  SEDGWICK_CANADA_BODY_PART_OPTIONS,
  SEDGWICK_CANADA_NATURE_OF_INJURY_OPTIONS,
  SEDGWICK_CANADA_CAUSE_OF_INJURY_OPTIONS,
  CANADA_PROVINCES,
} from "../sedgwickCanadaOptions";

export const sedgwickCanadaFormOverride: FormOverride = {
  state: {
    // Show body part fields
    [INCIDENT_INFORMATION_CONSTANTS.BODY_PART_INFORMATION_0]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.BODY_PART_INFORMATION_1]: {
      hidden: true,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_BODY_PART]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_BODY_PART]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_SIDE]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_SIDE]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.ADD_BODY_PART_NAME]: {
      hidden: false,
      ignoreInFinalForm: true,
    },
    [INCIDENT_INFORMATION_CONSTANTS.NATURE_OF_INJURY]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_NATURE_OF_INJURY]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    // Hide accident type (Canada doesn't use it)
    [INCIDENT_INFORMATION_CONSTANTS.ACCIDENT_TYPE]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    // Hide source of injury
    [INCIDENT_INFORMATION_CONSTANTS.SOURCE_OF_INJURY]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    // Hide initial medical treatment
    [PHYSICIAN_INFORMATION_CONSTANTS.INITIAL_MEDICAL_TREATMENT]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    // Show EMS fields (Canada-specific)
    [PHYSICIAN_INFORMATION_CONSTANTS.EMS_CALLED]: {
      hidden: false,
      ignoreInFinalForm: false,
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.EMS_ASSESSMENT]: {
      hidden: true,
      ignoreInFinalForm: false,
    },
    // Transported to hospital hidden until EMS called = Yes
    [PHYSICIAN_INFORMATION_CONSTANTS.TRANSPORTED_TO_HOSPITAL]: {
      hidden: true,
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.HOSPITAL_INFORMATION]: {
      hidden: true,
    },
    // Show referral reasons (Canada multi-select) and hide referral type (US single-select)
    [INCIDENT_INFORMATION_CONSTANTS.REFERRAL_REASONS]: {
      hidden: false,
      value: [],
    },
    [INCIDENT_INFORMATION_CONSTANTS.REFERRAL_TYPE]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
    // Override country fields to Canada
    [PHYSICIAN_INFORMATION_CONSTANTS.HOSPITAL_INFORMATION_COUNTRY]: {
      value: { value: "CA", label: "Canada" },
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_0_COUNTRY]: {
      value: { value: "CA", label: "Canada" },
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_1_COUNTRY]: {
      value: { value: "CA", label: "Canada" },
    },
    // Show available medical documents (Canada) and hide state forms (US)
    [FILE_ATTACHMENTS_CONSTANTS.AVAILABLE_MEDICAL_DOCUMENTS_ATTACHMENTS]: {
      hidden: false,
    },
    [FILE_ATTACHMENTS_CONSTANTS.STATE_FORMS]: {
      hidden: true,
      ignoreInFinalForm: true,
    },
  },
  config: {
    // Replace concerns options with Canada-specific list
    [INCIDENT_INFORMATION_CONSTANTS.QUESTIONABLE_CLAIM_REASONS]: {
      options: SEDGWICK_CANADA_CONCERNS_OPTIONS,
    },
    // Replace primary and secondary body part options
    [INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_BODY_PART]: {
      options: SEDGWICK_CANADA_BODY_PART_OPTIONS,
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_BODY_PART]: {
      options: SEDGWICK_CANADA_BODY_PART_OPTIONS,
    },
    [INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_SIDE]: {
      options: BODY_SIDE_OPTIONS,
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_SIDE]: {
      options: BODY_SIDE_OPTIONS,
    },
    // Replace nature of injury options
    [INCIDENT_INFORMATION_CONSTANTS.NATURE_OF_INJURY]: {
      options: SEDGWICK_CANADA_NATURE_OF_INJURY_OPTIONS,
    },
    // Replace cause of injury options
    [INCIDENT_INFORMATION_CONSTANTS.CAUSE_OF_INJURY]: {
      options: SEDGWICK_CANADA_CAUSE_OF_INJURY_OPTIONS,
    },
    // Body part field groups
    [INCIDENT_INFORMATION_CONSTANTS.BODY_PART_INFORMATION_0]: {
      contained: true,
    },
    [INCIDENT_INFORMATION_CONSTANTS.BODY_PART_INFORMATION_1]: {
      contained: true,
    },
    // Change "Hospital Name" to "Healthcare Facility Name"
    [PHYSICIAN_INFORMATION_CONSTANTS.HOSPITAL_INFORMATION_NAME]: {
      label: "Healthcare Facility Name",
    },
    // Change "State" to "Province" for healthcare facility address
    [PHYSICIAN_INFORMATION_CONSTANTS.HOSPITAL_INFORMATION_STATE]: {
      label: "Province",
      options: CANADA_PROVINCES,
    },
    // Change transported to hospital label
    [PHYSICIAN_INFORMATION_CONSTANTS.TRANSPORTED_TO_HOSPITAL]: {
      label:
        "Was the associate transported via ambulance to a healthcare facility?",
      description: "Enter healthcare facility information if available.",
    },
    // Change bypass label
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_AVAILABLE]: {
      label:
        "Did the associate seek medical attention prior to reporting to employer (bypass)?",
    },
    // Hardcode country to Canada
    [PHYSICIAN_INFORMATION_CONSTANTS.HOSPITAL_INFORMATION_COUNTRY]: {
      options: [{ value: "CA", label: "Canada" }],
      disabled: true,
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_0_COUNTRY]: {
      options: [{ value: "CA", label: "Canada" }],
      disabled: true,
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_1_COUNTRY]: {
      options: [{ value: "CA", label: "Canada" }],
      disabled: true,
    },
    // Change physician State dropdowns to Province with Canada provinces
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_0_STATE]: {
      label: "Province",
      options: CANADA_PROVINCES,
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_1_STATE]: {
      label: "Province",
      options: CANADA_PROVINCES,
    },
    // Update phone descriptions for Canada
    [PHYSICIAN_INFORMATION_CONSTANTS.HOSPITAL_INFORMATION_PHONE_WITH_EXTENSION_PHONE_NUMBER]:
      {
        description: "Please enter a valid phone number without country code",
      },
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_0_PHONE_WITH_EXTENSION_PHONE_NUMBER]:
      {
        description: "Please enter a valid phone number without country code",
      },
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_1_PHONE_WITH_EXTENSION_PHONE_NUMBER]:
      {
        description: "Please enter a valid phone number without country code",
      },
    // Show available medical documents (Canada) and hide state forms (US)
    [FILE_ATTACHMENTS_CONSTANTS.AVAILABLE_MEDICAL_DOCUMENTS_ATTACHMENTS]: {
      label: "Available Medical Documents",
    },
  },
  validation: {
    // Make symptoms list optional
    [INCIDENT_INFORMATION_CONSTANTS.SYMPTOMS_LIST]: {
      required: { value: false },
    },
    // Make EMS called required
    [PHYSICIAN_INFORMATION_CONSTANTS.EMS_CALLED]: {
      required: { value: true },
    },
    // Body part validations
    [INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_BODY_PART]: {
      required: { value: true },
    },
    [INCIDENT_INFORMATION_CONSTANTS.PRIMARY_INJURED_SIDE]: {
      required: { value: true },
    },
    [INCIDENT_INFORMATION_CONSTANTS.NATURE_OF_INJURY]: {
      required: { value: true },
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_INJURED_BODY_PART]: {
      required: {
        value: true,
        message: SECONDARY_INJURED_BODY_PART_REQUIRED_ERROR,
      },
    },
    // Hidden fields made optional
    [INCIDENT_INFORMATION_CONSTANTS.SOURCE_OF_INJURY]: {
      required: { value: false },
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.INITIAL_MEDICAL_TREATMENT]: {
      required: { value: false },
    },
    [INCIDENT_INFORMATION_CONSTANTS.SECONDARY_NATURE_OF_INJURY]: {
      required: { value: false },
    },
    [INCIDENT_INFORMATION_CONSTANTS.ACCIDENT_TYPE]: {
      required: { value: false },
    },
    // Override postal code validations for Canada (format: A1A 1A1)
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_0_ZIPCODE]: {
      allowedCharactersRegExp: {
        value: new RegExp(/^[a-zA-Z0-9 ]*$/),
        message: "Please enter only letters, numbers, and spaces",
      },
      maxCharacters: { value: 7 },
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.PHYSICIAN_INFORMATION_1_ZIPCODE]: {
      allowedCharactersRegExp: {
        value: new RegExp(/^[a-zA-Z0-9 ]*$/),
        message: "Please enter only letters, numbers, and spaces",
      },
      maxCharacters: { value: 7 },
    },
    [PHYSICIAN_INFORMATION_CONSTANTS.HOSPITAL_INFORMATION_ZIPCODE]: {
      allowedCharactersRegExp: {
        value: new RegExp(/^[a-zA-Z0-9 ]*$/),
        message: "Please enter only letters, numbers, and spaces",
      },
      maxCharacters: { value: 7 },
    },
  },
};
